import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import { useTranslations } from '../contexts/TranslationContext';
import { useLanguage } from '../contexts/LanguageContext';
import InlineTranslationEdit from './InlineTranslationEdit';
import { getWinnersForGameType } from '../data/drawsApi';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { mapWinnersFromApi } from '../utils/winnersApi';
import { getWinnerAmountText, getWinnerPrizeLabel, getWinnerTitle } from '../utils/winnerDisplay';
import { formatCurrencyValue } from '../utils/formatCurrency';

/**
 * A Chase the Ace row is a jackpot win ("Ace Found") only when the player
 * actually won the jackpot — i.e. isWinner === true and it is not a secondary
 * (weekly) prize. A row where the envelope has not been selected yet has
 * isWinner === false and no card, so it must NOT be treated as a jackpot win
 * (otherwise the target card leaks in as a fallback and the game is wrongly
 * marked "Complete").
 */
const isCtaJackpotWin = (row) =>
  !!row && row.isWinner === true && !(row.additionalDrawId || row.isSecondaryPrize || row.secondaryPrize);

/**
 * Determines whether a winner record represents a genuine grand/jackpot win
 * vs an additional draw / secondary prize.
 */
const isCardJackpotWin = (result, gameType) => {
  if (!result) return false;
  if (result.additionalDrawId || result.isSecondaryPrize || result.secondaryPrize) return false;
  if (gameType === 'chaseTheAce') {
    return isCtaJackpotWin(result);
  }
  if (gameType === 'superballBingo') {
    return result.isJackpot || result.isSuperballWinner || result.ballSelected === 76;
  }
  return result.isWinner !== false;
};

export default function RecentWinners({ gameType = 'classic5050', editMode, onTranslationUpdate, maxWinners, compact = false, vertical = false, onResults, weeklyFallback, grandPrizeFallback, ctaTargetCard }) {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const isPrizeDraw = gameType === 'prizeDraw';
  const isSquareGame = gameType === 'squareGame';
  const displayCount = maxWinners || 3;

  useEffect(() => {
    const loadWinners = async () => {
      setLoading(true);

      try {
        const { tlfId } = getConfig();
        const response = await api.get(`${apiRoutes.SiteWinners}?organizationId=${tlfId}`);
        const payload = response?.data?.payload || response?.data;
        const data = payload?.data || payload?.Data || payload || [];
        const mapped = mapWinnersFromApi(data, { language, gameType });
        const sliced = mapped.slice(0, displayCount);
        setResults(sliced);
        if (typeof onResults === 'function') onResults(sliced.length);
      } catch (error) {
        console.error('Failed to load recent winners:', error);
        const fallback = getWinnersForGameType(gameType, displayCount).map((winner) => ({
          id: winner.id,
          name: winner.name,
          amount: winner.amount,
          prize: winner.prize || null,
          squareNumber: winner.squareNumber,
          date: winner.date,
          draw: winner.draw,
          avatar: winner.avatar,
          color: winner.color,
          isWinner: true,
          gameType: winner.gameType || gameType,
          reason: ''
        }));
        setResults(fallback);
        if (typeof onResults === 'function') onResults(fallback.length);
      } finally {
        setLoading(false);
      }
    };

    loadWinners();
  }, [gameType, language, displayCount]);

  const isSuperballBingo = gameType === 'superballBingo';
  const isChaseTheAce = gameType === 'chaseTheAce';

  // For CTA compact+vertical mode: group rows by game number so players can
  // see game boundaries — "Game 2 Complete" / "Game 3 — Active".
  const buildCtaGroups = (rows) => {
    const groups = [];
    const seen = new Map(); // gameNumber → group index
    for (const row of rows) {
      const gn = row.gameNumber ?? null;
      const key = gn ?? '__none__';
      if (!seen.has(key)) {
        seen.set(key, groups.length);
        groups.push({ gameNumber: gn, rows: [] });
      }
      groups[seen.get(key)].rows.push(row);
    }
    // Sort games descending (newest game first).
    groups.sort((a, b) => (b.gameNumber ?? -1) - (a.gameNumber ?? -1));
    // Within each game: sort by weekNumber descending, jackpot winner first within same week.
    for (const g of groups) {
      g.rows.sort((a, b) => {
        const wDiff = (b.weekNumber ?? 0) - (a.weekNumber ?? 0);
        if (wDiff !== 0) return wDiff;
        const aIsJackpot = isCtaJackpotWin(a);
        const bIsJackpot = isCtaJackpotWin(b);
        return (bIsJackpot ? 1 : 0) - (aIsJackpot ? 1 : 0);
      });
    }
    // Mark groups as complete if they contain a jackpot winner (ace found).
    // Active = highest game number group that is not complete.
    const marked = groups.map((g) => ({
      ...g,
      isComplete: g.rows.some(r => isCtaJackpotWin(r)),
    }));
    const activeIdx = marked.findIndex(g => !g.isComplete);
    return marked.map((g, i) => ({ ...g, isActive: i === activeIdx }));
  };

  // Compact mode for embedding in sidebars
  if (compact) {
    // CTA-specific grouped rendering
    if (isChaseTheAce && vertical) {
      const groups = buildCtaGroups(results);
      const hasAnyRows = groups.some(g => g.rows.length > 0);
      return (
        <div className="space-y-1">
          {!loading && !hasAnyRows && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Trophy className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('compactWinners.noWinnersYet', 'No winners yet — check back after the next draw!')}
              </p>
            </div>
          )}
          {groups.map((group) => (
            <div key={group.gameNumber ?? 'no-game'}>
              {/* Game header divider */}
              {group.gameNumber != null && (
                group.isComplete ? (
                  // Completed game — subtle "sealed" look
                  <div className="flex items-center gap-2 px-1 py-2 mt-1">
                    <div className="flex-1 h-px bg-gradient-to-r from-amber-300/60 to-transparent dark:from-amber-600/40" />
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700/60 shadow-sm">
                      <span className="text-base leading-none">🏆</span>
                      <span className="text-[11px] font-bold text-amber-700 dark:text-amber-400 whitespace-nowrap">
                        {t('recentWinners.game', 'Game')} {group.gameNumber} — {t('recentWinners.gameComplete', 'Complete')}
                      </span>
                      <span className="text-[11px] text-amber-700 dark:text-amber-400">· {t('recentWinners.aceFound', 'Ace Found!')}</span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-amber-300/60 to-transparent dark:from-amber-600/40" />
                  </div>
                ) : (
                  // Active game
                  <div className="flex items-center gap-2 px-1 py-2 mt-1">
                    <div className="flex-1 h-px bg-gradient-to-r from-charity-primary/40 to-transparent" />
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-charity-primary/10 dark:bg-charity-primary/20 border border-charity-primary/30 dark:border-charity-primary/40">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                      </span>
                      <span className="text-[11px] font-bold text-charity-primary-dark dark:text-charity-primary-light whitespace-nowrap">
                        {t('recentWinners.game', 'Game')} {group.gameNumber} — {t('recentWinners.gameActive', 'Active')}
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-charity-primary/40 to-transparent" />
                  </div>
                )
              )}
              {/* Winner rows for this game */}
              <div className="space-y-2">
                {group.rows.map((result) => {
                  const isCtaJackpot = isCtaJackpotWin(result);
                  const rawAmount = result.amount || (isCtaJackpot ? grandPrizeFallback : weeklyFallback) || null;
                  const winAmount = rawAmount != null && rawAmount >= 0 ? rawAmount : null;

                  // Card parsing (shared)
                  const suitRaw = String(result.cardSuit || '').toLowerCase().trim();
                  const suitMap = {
                    '♥': { glyph: '♥', red: true }, '♦': { glyph: '♦', red: true },
                    '♣': { glyph: '♣', red: false }, '♠': { glyph: '♠', red: false },
                    heart: { glyph: '♥', red: true }, hearts: { glyph: '♥', red: true },
                    diamond: { glyph: '♦', red: true }, diamonds: { glyph: '♦', red: true },
                    club: { glyph: '♣', red: false }, clubs: { glyph: '♣', red: false },
                    spade: { glyph: '♠', red: false }, spades: { glyph: '♠', red: false },
                  };
                  const fallbackRank = ctaTargetCard?.rank || 'A';
                  const fallbackSuit = ctaTargetCard?.suit || '♠';
                  const suit = suitMap[suitRaw] || { glyph: fallbackSuit, red: fallbackSuit === '♥' || fallbackSuit === '♦' };
                  const rankMap = { jack: 'J', queen: 'Q', king: 'K', ace: 'A' };
                  const rank = rankMap[String(result.cardNumber || result.cardDrawn || '').trim().toLowerCase()] || String(result.cardNumber || result.cardDrawn || '').trim() || fallbackRank;

                  // Week label: just the week portion (game header already shown above)
                  const weekLabel = result.weekNumber != null ? `${t('winners.winnerMeta.week', 'Week')} ${result.weekNumber}` : result.draw;

                  return (
                    <div
                      key={result.id}
                      className={`group relative overflow-hidden flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 pl-3.5 sm:pl-4 rounded-xl transition-all duration-200 ${
                        isCtaJackpot
                          ? 'bg-gradient-to-r from-amber-500/15 via-yellow-500/10 to-amber-500/5 dark:from-amber-500/20 dark:via-yellow-500/15 dark:to-amber-500/5 border border-amber-400/50 dark:border-amber-400/40 shadow-xs'
                          : 'bg-white/90 dark:bg-white/[0.04] hover:bg-gray-50 dark:hover:bg-white/[0.07] border border-gray-200/80 dark:border-white/10 shadow-xs'
                      }`}
                    >
                      {/* Left vertical accent indicator */}
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-1 ${
                          isCtaJackpot
                            ? 'bg-gradient-to-b from-amber-400 to-yellow-500'
                            : 'bg-purple-500/80 dark:bg-purple-400'
                        }`}
                      />
                      <div
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full shrink-0 flex items-center justify-center text-white font-bold text-xs sm:text-sm bg-gradient-to-br ${result.color} ${
                          isCtaJackpot
                            ? 'ring-2 ring-amber-400 ring-offset-1 dark:ring-offset-gray-900 shadow-md'
                            : 'shadow-xs'
                        }`}
                      >
                        {result.avatar}
                      </div>
                      <div className="flex-1 min-w-0 py-0.5">
                        <p
                          className={`font-bold text-sm sm:text-base leading-snug line-clamp-2 break-words ${
                            isCtaJackpot ? 'text-amber-950 dark:text-amber-100' : 'text-gray-900 dark:text-white'
                          }`}
                          title={result.name}
                        >
                          {result.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <span
                            className={`text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded leading-none shrink-0 ${
                              isCtaJackpot
                                ? 'bg-amber-200/90 dark:bg-amber-800/80 text-amber-950 dark:text-amber-100 border border-amber-300/80 dark:border-amber-700/60'
                                : 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-200/60 dark:border-purple-800/50'
                            }`}
                          >
                            {isCtaJackpot ? `🏆 ${t('recentWinners.jackpot', 'Jackpot')}` : getWinnerTitle(result, t)}
                          </span>
                          {weekLabel && (
                            <span className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 shrink-0">
                              {weekLabel}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Card drawn - FIXED COLUMN WIDTH for straight vertical alignment */}
                      <div className="w-11 shrink-0 flex flex-col items-center justify-center">
                        {isCtaJackpot ? (
                          <>
                            <div
                              className={`relative w-9 h-12 rounded-md bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-400 shadow-md border-2 border-amber-500 ring-1 ring-amber-300/60 ${
                                suit.red ? 'text-red-700' : 'text-gray-900'
                              } flex items-center justify-center`}
                            >
                              <span className="absolute top-0.5 left-1 text-[10px] font-bold leading-none">{rank}</span>
                              <span className="text-lg leading-none">{suit.glyph}</span>
                            </div>
                            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 mt-0.5 leading-none tracking-tight">
                              {rank}{suit.glyph}!
                            </span>
                          </>
                        ) : result.cardDrawn ? (
                          <>
                            <div
                              className={`relative w-9 h-12 rounded-md bg-white shadow-xs border border-gray-200 dark:border-gray-600 ${
                                suit.red ? 'text-red-600' : 'text-gray-900'
                              } flex items-center justify-center`}
                            >
                              <span className="absolute top-0.5 left-1 text-[10px] font-bold leading-none">{rank}</span>
                              <span className="text-lg leading-none">{suit.glyph}</span>
                            </div>
                            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mt-0.5 leading-none tracking-tight">
                              {rank}{suit.glyph}
                            </span>
                          </>
                        ) : (
                          <div className="w-9 h-12 rounded-md border border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-400 text-xs">
                            ?
                          </div>
                        )}
                      </div>
                      {/* Prize Amount - FIXED / MIN-WIDTH COLUMN with tabular nums to prevent card shifting */}
                      <div className="w-20 sm:w-24 shrink-0 text-right flex flex-col items-end justify-center">
                        {winAmount != null && (
                          <p
                            className={`font-black tabular-nums tracking-tight leading-tight whitespace-nowrap ${
                              winAmount >= 1000000
                                ? 'text-xs sm:text-sm'
                                : winAmount >= 100000
                                  ? 'text-xs sm:text-base'
                                  : 'text-sm sm:text-base'
                            } ${
                              isCtaJackpot
                                ? 'text-amber-600 dark:text-amber-300'
                                : 'text-emerald-600 dark:text-emerald-400'
                            }`}
                          >
                            {formatCurrencyValue(winAmount, { language })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={vertical ? 'space-y-3' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
        {results.map((result, index) => {
          const isJackpotWin = isCardJackpotWin(result, gameType);
          const isSuperball = isJackpotWin;
          // CTA jackpot winner = found the Ace (actually won, not a secondary/weekly prize)
          const isCtaJackpot = isChaseTheAce && isCtaJackpotWin(result);
          const fallbackForRow = isCtaJackpot ? grandPrizeFallback : weeklyFallback;
          const rawAmount = result.amount || (isSuperballBingo && isSuperball ? 12500 : null) || fallbackForRow || null;
          const winAmount = rawAmount != null && rawAmount >= 0 ? rawAmount : null;
          
          let compactCardBgClass = 'bg-gray-50/80 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border-l-4 border-blue-500';
          if (isJackpotWin) {
            compactCardBgClass = 'bg-amber-500/10 dark:bg-amber-500/10 border-l-4 border-amber-400';
          } else if (result.additionalDrawId || result.isPrize) {
            compactCardBgClass = 'bg-purple-500/10 dark:bg-purple-500/10 border-l-4 border-purple-400';
          }

          return (
            <div
              key={result.id}
              className={`rounded-xl p-3.5 sm:p-4 transition-all duration-300 group cursor-pointer ${compactCardBgClass}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${result.color} rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity`}></div>
                  <div className={`relative w-14 h-14 bg-gradient-to-br ${result.color} rounded-full flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform`}>
                    {result.avatar}
                  </div>
                  {isSuperballBingo && isSuperball && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-amber-900 font-bold text-xs">★</span>
                    </div>
                  )}
                  {isSuperballBingo && !isSuperball && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-xs">✓</span>
                    </div>
                  )}
                  {!result.isWinner && !isSuperballBingo && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-xs">↻</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-lg transition-colors ${
                    isSuperballBingo
                      ? isSuperball
                        ? 'text-amber-700 dark:text-amber-400'
                        : 'text-emerald-700 dark:text-emerald-400'
                      : result.isWinner
                        ? 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400'
                        : 'text-gray-900 dark:text-white'
                  }`}>
                    {result.name}
                  </p>
                  <div className="flex items-center gap-2">
                    {isSuperballBingo && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        isSuperball 
                          ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900'
                          : 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300'
                      }`}>
                        {getWinnerTitle({ ...result, isJackpot: isSuperball }, t).toUpperCase()}
                      </span>
                    )}
                    {!isSuperballBingo && <p className="text-xs text-gray-500 dark:text-gray-400">{result.draw}</p>}
                  </div>
                  {isSuperballBingo && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{result.draw}</p>}
                </div>
              </div>

              {/* Superball Bingo specific: Ball drawn + Amount */}
              {isSuperballBingo && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    {/* Ball Drawn */}
                    <div className={`flex-1 rounded-lg p-3 border ${
                      isSuperball
                        ? 'bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/40 dark:to-yellow-900/40 border-amber-200 dark:border-amber-700'
                        : 'bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                    }`}>
                      <p className={`text-xs font-medium mb-1 ${isSuperball ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                        Ball Drawn
                      </p>
                      <div className="flex items-center gap-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          isSuperball 
                            ? 'bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 text-amber-900 shadow-lg'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                        }`}>
                          {isSuperball ? '★' : ballDrawn}
                        </div>
                        {isSuperball && <span className="text-amber-600 dark:text-amber-400 font-bold text-sm">SUPERBALL!</span>}
                      </div>
                    </div>
                    
                    {/* Amount Won */}
                    <div className={`flex-1 rounded-lg p-3 border relative overflow-hidden ${
                      isSuperball
                        ? 'bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/40 dark:to-yellow-900/40 border-amber-200 dark:border-amber-700'
                        : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-100 dark:border-green-800'
                    }`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity animate-shimmer"></div>
                      <p className={`text-xs font-medium mb-1 relative z-10 ${
                        isSuperball ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
                      }`}>
                        Amount Won
                      </p>
                      <p className={`text-2xl font-bold relative z-10 ${
                        isSuperball ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {formatCurrencyValue(winAmount, { language })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Standard game types */}
              {!isSuperballBingo && (
                <div className="space-y-3">
                  {result.isWinner ? (
                    <>
                      {isSquareGame && (result.squareNumber || result.square) && (
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 rounded-lg p-3 border border-emerald-100 dark:border-emerald-800 group-hover:border-emerald-300 dark:group-hover:border-emerald-600 transition-colors">
                          <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium mb-1">
                            <InlineTranslationEdit
                              translationKey="recentWinners.winningSquare"
                              value={t('recentWinners.winningSquare')}
                              fallback="Winning Square"
                              onUpdate={onTranslationUpdate}
                              editMode={editMode}
                              className="text-xs font-medium"
                            />
                          </p>
                          <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">#{result.squareNumber || result.square}</p>
                        </div>
                      )}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg p-3 border border-green-100 dark:border-green-800 group-hover:border-green-300 dark:group-hover:border-green-600 transition-colors relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity animate-shimmer"></div>
                        <p className="text-xs font-medium mb-1 relative z-10" style={{ color: isJackpotWin ? '#b45309' : result.additionalDrawId || result.isPrize ? '#7e22ce' : '#15803d' }}>
                          <InlineTranslationEdit
                            translationKey={result.additionalDrawId ? "recentWinners.prizeWon" : "recentWinners.jackpot"}
                            value={result.additionalDrawId ? t('recentWinners.prizeWon') : t('recentWinners.jackpot')}
                            fallback={getWinnerPrizeLabel(result, t)}
                            onUpdate={onTranslationUpdate}
                            editMode={editMode}
                            className="text-xs font-medium"
                          />
                        </p>
                        {isPrizeDraw ? (
                          <p className="text-lg font-bold relative z-10" style={{ color: isJackpotWin ? '#b45309' : result.additionalDrawId || result.isPrize ? '#7e22ce' : '#15803d' }}>{getWinnerAmountText(result, { language })}</p>
                        ) : (
                          <p className="text-2xl font-bold relative z-10" style={{ color: isJackpotWin ? '#b45309' : result.additionalDrawId || result.isPrize ? '#7e22ce' : '#15803d' }}>{getWinnerAmountText(result, { language })}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                      <p className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-1">
                        <InlineTranslationEdit
                          translationKey="recentWinners.notEligible"
                          value={t('recentWinners.notEligible')}
                          fallback="No Winner"
                          onUpdate={onTranslationUpdate}
                          editMode={editMode}
                          className="text-xs font-medium"
                        />
                      </p>
                      <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">{result.reason}</p>
                      <p className="text-xs text-amber-500 dark:text-amber-500 mt-1">
                        <InlineTranslationEdit
                          translationKey="recentWinners.rollover"
                          value={t('recentWinners.rollover')}
                          fallback="Jackpot rolled over to next draw"
                          onUpdate={onTranslationUpdate}
                          editMode={editMode}
                          className="text-xs"
                        />
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="w-4 h-4 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" />
                    <span className="group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">{result.date}</span>
                  </div>
                </div>
              )}

              {/* Date for Superball Bingo */}
              {isSuperballBingo && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-3">
                  <Calendar className="w-4 h-4 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors" />
                  <span className="group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">{result.date}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-white/[0.03] dark:backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 dark:border-white/10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-6">
        <div className="flex items-center gap-3 group">
          <div className="bg-gradient-to-br from-orange-500 to-amber-500 p-2 rounded-lg group-hover:scale-110 transition-transform shadow-lg">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            <InlineTranslationEdit
              translationKey="recentWinners.title"
              value={t('recentWinners.title')}
              fallback="Recent Results"
              onUpdate={onTranslationUpdate}
              editMode={editMode}
              className="text-2xl font-bold"
            />
          </h3>
          <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse" />
        </div>
        <button
          onClick={(e) => {
            if (editMode) {
              e.preventDefault();
              return;
            }
            navigate('/winners');
          }}
          className={`text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm px-4 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-all hover:scale-105 active:scale-95 ${editMode ? 'cursor-default' : ''}`}
        >
          <InlineTranslationEdit
            translationKey="recentWinners.viewAll"
            value={t('recentWinners.viewAll')}
            fallback="View All Results"
            onUpdate={onTranslationUpdate}
            editMode={editMode}
            className="font-medium text-sm"
          />
        </button>
      </div>

      <div className={vertical ? 'space-y-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'}>
        {!loading && results.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 p-4 rounded-2xl mb-4">
              <Trophy className="w-10 h-10 text-orange-400" />
            </div>
            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              {t('recentWinners.noWinnersTitle', 'No Winners Yet')}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
              {t('recentWinners.noWinnersDesc', 'Winners will appear here after the next draw. Get your tickets now for a chance to win!')}
            </p>
          </div>
        )}
        {results.map((result, index) => {
          const isJackpotWin = isCardJackpotWin(result, gameType);
          const isSuperball = isJackpotWin;
          const fallbackForRow = result.isWinner ? grandPrizeFallback : weeklyFallback;
          const rawAmount = result.amount || (isSuperballBingo && isSuperball ? 12500 : null) || fallbackForRow || null;
          const winAmount = rawAmount != null && rawAmount >= 0 ? rawAmount : null;
          
          let cardGradientClass = 'from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-950/10 border-green-200 dark:border-green-800/40';
          if (isJackpotWin) {
            cardGradientClass = 'from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-amber-200 dark:border-amber-800/40';
          } else if (result.additionalDrawId || result.isPrize) {
            cardGradientClass = 'from-purple-50 to-fuchsia-50 dark:from-purple-900/20 dark:to-fuchsia-950/20 border-purple-200 dark:border-purple-800/40';
          }

          return (
            <div
              key={result.id}
              className={`bg-gradient-to-br rounded-xl p-5 border hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer ${cardGradientClass}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${result.color} rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity`}></div>
                  <div className={`relative w-14 h-14 bg-gradient-to-br ${result.color} rounded-full flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform`}>
                    {result.avatar}
                  </div>
                  {isSuperballBingo && isSuperball && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-amber-900 font-bold text-xs">★</span>
                    </div>
                  )}
                  {isSuperballBingo && !isSuperball && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-xs">✓</span>
                    </div>
                  )}
                  {!result.isWinner && !isSuperballBingo && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-xs">↻</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-lg transition-colors ${
                    isSuperballBingo
                      ? isSuperball
                        ? 'text-amber-700 dark:text-amber-400'
                        : 'text-emerald-700 dark:text-emerald-400'
                      : result.isWinner
                        ? 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400'
                        : 'text-gray-900 dark:text-white'
                  }`}>
                    {result.name}
                  </p>
                  <div className="flex items-center gap-2">
                    {isSuperballBingo && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        isSuperball 
                          ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900'
                          : 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300'
                      }`}>
                        {getWinnerTitle({ ...result, isJackpot: isSuperball }, t).toUpperCase()}
                      </span>
                    )}
                    {!isSuperballBingo && <p className="text-xs text-gray-500 dark:text-gray-400">{result.draw}</p>}
                  </div>
                  {isSuperballBingo && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{result.draw}</p>}
                </div>
              </div>

              {/* Superball Bingo specific: Ball drawn + Amount */}
              {isSuperballBingo && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    {/* Ball Drawn */}
                    <div className={`flex-1 rounded-lg p-3 border ${
                      isSuperball
                        ? 'bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/40 dark:to-yellow-900/40 border-amber-200 dark:border-amber-700'
                        : 'bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                    }`}>
                      <p className={`text-xs font-medium mb-1 ${isSuperball ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                        Ball Drawn
                      </p>
                      <div className="flex items-center gap-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          isSuperball 
                            ? 'bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 text-amber-900 shadow-lg'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                        }`}>
                          {isSuperball ? '★' : ballDrawn}
                        </div>
                        {isSuperball && <span className="text-amber-600 dark:text-amber-400 font-bold text-sm">SUPERBALL!</span>}
                      </div>
                    </div>
                    
                    {/* Amount Won */}
                    <div className={`flex-1 rounded-lg p-3 border relative overflow-hidden ${
                      isSuperball
                        ? 'bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/40 dark:to-yellow-900/40 border-amber-200 dark:border-amber-700'
                        : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-100 dark:border-green-800'
                    }`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity animate-shimmer"></div>
                      <p className={`text-xs font-medium mb-1 relative z-10 ${
                        isSuperball ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
                      }`}>
                        Amount Won
                      </p>
                      <p className={`text-2xl font-bold relative z-10 ${
                        isSuperball ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {formatCurrencyValue(winAmount, { language })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Standard game types */}
              {!isSuperballBingo && (
                <div className="space-y-3">
                  {result.isWinner ? (
                    <>
                      {isSquareGame && (result.squareNumber || result.square) && (
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 rounded-lg p-3 border border-emerald-100 dark:border-emerald-800 group-hover:border-emerald-300 dark:group-hover:border-emerald-600 transition-colors">
                          <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium mb-1">
                            <InlineTranslationEdit
                              translationKey="recentWinners.winningSquare"
                              value={t('recentWinners.winningSquare')}
                              fallback="Winning Square"
                              onUpdate={onTranslationUpdate}
                              editMode={editMode}
                              className="text-xs font-medium"
                            />
                          </p>
                          <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">#{result.squareNumber || result.square}</p>
                        </div>
                      )}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg p-3 border border-green-100 dark:border-green-800 group-hover:border-green-300 dark:group-hover:border-green-600 transition-colors relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity animate-shimmer"></div>
                        <p className="text-xs font-medium mb-1 relative z-10" style={{ color: isJackpotWin ? '#b45309' : result.additionalDrawId || result.isPrize ? '#7e22ce' : '#15803d' }}>
                          <InlineTranslationEdit
                            translationKey={result.additionalDrawId ? "recentWinners.prizeWon" : "recentWinners.jackpot"}
                            value={result.additionalDrawId ? t('recentWinners.prizeWon') : t('recentWinners.jackpot')}
                            fallback={getWinnerPrizeLabel(result, t)}
                            onUpdate={onTranslationUpdate}
                            editMode={editMode}
                            className="text-xs font-medium"
                          />
                        </p>
                        {isPrizeDraw ? (
                          <p className="text-lg font-bold relative z-10" style={{ color: isJackpotWin ? '#b45309' : result.additionalDrawId || result.isPrize ? '#7e22ce' : '#15803d' }}>{getWinnerAmountText(result, { language })}</p>
                        ) : (
                          <p className="text-2xl font-bold relative z-10" style={{ color: isJackpotWin ? '#b45309' : result.additionalDrawId || result.isPrize ? '#7e22ce' : '#15803d' }}>{getWinnerAmountText(result, { language })}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                      <p className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-1">
                        <InlineTranslationEdit
                          translationKey="recentWinners.notEligible"
                          value={t('recentWinners.notEligible')}
                          fallback="No Winner"
                          onUpdate={onTranslationUpdate}
                          editMode={editMode}
                          className="text-xs font-medium"
                        />
                      </p>
                      <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">{result.reason}</p>
                      <p className="text-xs text-amber-500 dark:text-amber-500 mt-1">
                        <InlineTranslationEdit
                          translationKey="recentWinners.rollover"
                          value={t('recentWinners.rollover')}
                          fallback="Jackpot rolled over to next draw"
                          onUpdate={onTranslationUpdate}
                          editMode={editMode}
                          className="text-xs"
                        />
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="w-4 h-4 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" />
                    <span className="group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">{result.date}</span>
                  </div>
                </div>
              )}

              {/* Date for Superball Bingo */}
              {isSuperballBingo && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-3">
                  <Calendar className="w-4 h-4 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors" />
                  <span className="group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">{result.date}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
